"use client"

import styled from "@emotion/styled"
import { imageGalleryInteraction } from "@eurodrive/utils/gtm"
import { IconButton } from "@mui/material"
import { DetailedVehicleImage } from "api"
import Image from "next/image"
import { Navigation } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"

const StyledLayout = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 50px;
  }
`

// Styled components (follow existing GridSwiper patterns)
const StyledSwiper = styled(Swiper)`
  position: relative;
  width: 100%;
  max-width: 630px;
  margin: 0 auto;
  height: 348px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;

  img {
    object-fit: cover;
  }

  .swiper-slide {
    &:focus {
      outline: 2px solid #000;
      outline-offset: -2px;
    }
  }
`

const NavigationButtons = styled.div`
  display: flex;
  gap: 16px;
  z-index: 10;
  justify-content: center;
`

// Match GridSwiper arrow style (consistency with design system)
const NavArrow = styled(IconButton)`
  width: 32px;
  height: 32px;
  padding: 10px 0; /* replicates GridSwiper */
  border-radius: 4px !important;
  background: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid var(--Eurodrive-Lighter-Grey, #ece9e9);
  &:hover {
    background: #ffffff;
  }
  &::after {
    display: none;
  }
`

export default function Gallery({
  pictures,
  vehicleId = "unknown",
  bypassFilter = false,
}: {
  pictures: DetailedVehicleImage[]
  vehicleId?: string
  bypassFilter?: boolean
}) {
  // Filter to close-up cu-1 .. cu-17 inclusive, unless bypassFilter is set
  const filtered: DetailedVehicleImage[] = (pictures || []).filter((img) => {
    if (!img?.url) return false
    if (bypassFilter) return true
    const match = img.url.match(/closeups\/cu-(\d+)\.(?:jpg|jpeg)$/i)
    if (!match) return false
    const num = parseInt(match[1], 10)
    return num >= 1 && num <= 17
  })

  if (!filtered.length) return null

  const showNav = filtered.length > 1

  const handleSlideChange = (swiper: any) => {
    imageGalleryInteraction(vehicleId, String(swiper.activeIndex + 1))
  }

  const handleNavClick = () => {
    // Track navigation button clicks with current index
    // Note: Swiper will trigger onSlideChange after navigation
  }

  const handleKeyDown = (event: React.KeyboardEvent, swiper: any) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault()
      if (event.key === "ArrowRight") {
        swiper.slidePrev()
      } else {
        swiper.slideNext()
      }
    }
  }

  return (
    <StyledLayout>
      <StyledSwiper
        aria-label={`גלריית תקריבים, ${filtered.length} תמונות. השתמש בחיצים ימינה ושמאלה לניווט`}
        dir="rtl"
        modules={[Navigation]}
        slidesPerView={1}
        navigation={
          showNav
            ? { prevEl: ".swiper-button-prev", nextEl: ".swiper-button-next" }
            : undefined
        }
        onSlideChange={handleSlideChange}
        onSwiper={(swiper) => {
          const container = swiper.el
          container.setAttribute("tabindex", "0")
          container.setAttribute("role", "region")
          container.addEventListener("keydown", (e) =>
            handleKeyDown(e as any, swiper),
          )
        }}
        style={{ width: "100%" }}
      >
        {filtered.map((img, idx) => (
          <SwiperSlide
            key={img.url}
            aria-label={`תמונה ${idx + 1} מתוך ${filtered.length}`}
          >
            <Image
              src={getUmbracoMediaUrl(img.url)}
              alt={img.label?.trim() || `תקריב ${idx + 1}`}
              fill
              sizes="(max-width: 900px) 100vw, 800px"
              priority={idx === 0}
            />
          </SwiperSlide>
        ))}
      </StyledSwiper>
      {showNav && (
        <NavigationButtons role="group" aria-label="ניווט גלריה">
          <NavArrow
            className="swiper-button-prev"
            aria-label="לתמונה הקודמת"
            onClick={handleNavClick}
          >
            <Image
              src="/icons/chevron-right.svg"
              alt="קדימה"
              width={12}
              height={12}
            />
          </NavArrow>
          <NavArrow
            className="swiper-button-next"
            aria-label="לתמונה הבאה"
            onClick={handleNavClick}
          >
            <Image
              src="/icons/chevron-left.svg"
              alt="אחורה"
              width={12}
              height={12}
            />
          </NavArrow>
        </NavigationButtons>
      )}
    </StyledLayout>
  )
}
